// Floating on-plan toolbar for a selected cable SOURCE (distribution board).
// Appears at the cursor where the source marker was clicked (rendered into the
// fixed-position slot ReviewPageV3 owns). Compact white card with 4 actions:
//   • Move   -> begin a source grab (user then clicks the new spot)
//   • Remove -> hide the source from the plan
//   • Edit   -> inline rename (display name only)
//   • Info   -> "Verteiler info" panel: what we know about this board
// Closes on any action; outside-click + Esc are handled by ReviewPageV3.

import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { formatMetres } from "./formatMetres";
import {
  Move,
  Trash2,
  Pencil,
  Info,
  X,
  Check,
  RotateCcw,
  TriangleAlert,
  ArrowUpDown,
} from "lucide-react";
import clsx from "clsx";
import {
  shortBoardLabel,
  type CableTakeoffGeometry,
} from "../utils/fetchCableTakeoffPaths";
import type { CableProvenance } from "../utils/fetchCables";

interface SourceStats {
  circuitCount: number;
  totalM: number;
}

export interface SourceToolbarProps {
  /** Marker label of the selected source (e.g. "UV3"). */
  label: string;
  /** Effective geometry (for circuits-fed / total-metres stats). */
  geometry: CableTakeoffGeometry | null;
  /** Effective per-circuit total length in metres. */
  lengthsByCircuit: Record<string, number>;
  /** Display-name overrides keyed by marker label. */
  sourceNames: Record<string, string>;
  /** Cable provenance (for the placement note in Info). */
  provenance?: CableProvenance | null;
  startMoveSource: (label: string) => void;
  removeSource: (label: string) => void;
  renameSource: (label: string, name: string) => void;
  resetSource: (label: string) => void;
  onClose: () => void;
  /** False until Karl's steps are done: nothing is calculated before. */
  isTotalShown?: boolean;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function SourceToolbar({
  label,
  geometry,
  lengthsByCircuit,
  sourceNames,
  provenance,
  startMoveSource,
  removeSource,
  renameSource,
  resetSource,
  onClose,
  isTotalShown = true,
}: SourceToolbarProps) {
  const { t, i18n } = useTranslation();
  const displayName = sourceNames[label] ?? label;
  const [mode, setMode] = useState<"menu" | "rename" | "info">("menu");
  const [nameDraft, setNameDraft] = useState(displayName);

  // Circuits fed by this board = circuits whose start waypoint is nearest to a
  // marker carrying this label; total = sum of those circuits' lengths.
  const stats = useMemo<SourceStats>(() => {
    if (!geometry) return { circuitCount: 0, totalM: 0 };
    const markers = geometry.sources;
    if (markers.length === 0) return { circuitCount: 0, totalM: 0 };
    let circuitCount = 0;
    let totalM = 0;
    for (const [cid, state] of Object.entries(geometry.byCircuit)) {
      const start = state.paths[0]?.waypoints[0];
      if (!start) continue;
      let bestLabel: string | null = null;
      let bestD2 = Infinity;
      for (const m of markers) {
        const dx = m.x - start.x;
        const dy = m.y - start.y;
        const dd = dx * dx + dy * dy;
        if (dd < bestD2) {
          bestD2 = dd;
          bestLabel = m.label;
        }
      }
      if (bestLabel === label) {
        circuitCount += 1;
        totalM += lengthsByCircuit[cid] ?? 0;
      }
    }
    return { circuitCount, totalM };
  }, [geometry, lengthsByCircuit, label]);

  // Placement note from provenance (matched by short board label).
  const provNote = useMemo(() => {
    const src = provenance?.sources?.find(
      (s) => shortBoardLabel(s.name) === label,
    );
    return src
      ? {
          name: src.name,
          placedBy: src.placed_by,
          origin: src.origin,
          estimated: src.estimated,
        }
      : null;
  }, [provenance, label]);

  // The agent estimated this source (no real board symbol / PDF text found here).
  // Prefer the explicit flag; fall back to the text heuristic for older files.
  const isEstimated = useMemo(() => {
    if (provNote?.estimated !== undefined) return provNote.estimated;
    const re =
      /estimat|cent(?:re|er) of the devices|no board|approx|assum|guess|unsure/i;
    return !!provNote?.placedBy && re.test(provNote.placedBy);
  }, [provNote]);

  // Panel is known but this dot marks the riser entry on this floor, not the
  // panel itself (origin = plan_text + placed_by mentions the riser). Derived
  // the same way the foundation classifier does: estimated flag wins first.
  const isRiser = useMemo(() => {
    if (isEstimated) return false;
    return (
      provNote?.origin === "plan_text" &&
      !!provNote?.placedBy &&
      /riser|steigleit/i.test(provNote.placedBy)
    );
  }, [isEstimated, provNote]);

  const submitRename = () => {
    const nextName = nameDraft.trim();
    if (nextName !== "" && nextName !== displayName) {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_FEED_POINT_CHANGED, {
        action: "renamed",
      });
    }
    renameSource(label, nextName);
    onClose();
  };

  return (
    <div
      // Offset slightly from the cursor so the marker stays visible.
      className="w-56 translate-x-2 translate-y-2 overflow-hidden rounded-lg border border-slate-200 bg-white text-xs shadow-lg"
    >
      {/* header */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 truncate">
          <span
            className={clsx(
              "grid h-5 w-5 shrink-0 place-items-center rounded text-[10px] font-bold",
              isEstimated
                ? "bg-rose-100 text-rose-700"
                : "bg-amber-100 text-amber-700",
            )}
          >
            {label}
          </span>
          <span
            className="truncate font-semibold text-slate-700"
            title={displayName}
          >
            {displayName}
          </span>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          title={t("smartSurveyor.cablesBeta.close")}
        >
          <X size={13} />
        </button>
      </div>

      {/* estimated-source warning */}
      {isEstimated && (
        <div className="flex items-start gap-1.5 border-b border-rose-100 bg-rose-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-rose-700">
          <TriangleAlert size={13} className="mt-0.5 shrink-0" />
          <span>{t("smartSurveyor.cablesBeta.estimatedSourceWarning")}</span>
        </div>
      )}

      {/* riser-entry info (panel is known; this dot is the riser, not the panel) */}
      {!isEstimated && isRiser && (
        <div className="flex items-start gap-1.5 border-b border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-blue-700">
          <ArrowUpDown size={13} className="mt-0.5 shrink-0" />
          <span>{t("smartSurveyor.cablesBeta.riserSourceInfo")}</span>
        </div>
      )}

      {mode === "rename" ? (
        <div className="p-2">
          <div className="mb-1 text-[11px] text-slate-500">
            {t("smartSurveyor.cablesBeta.renameSourceTitle")}
          </div>
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitRename();
              else if (e.key === "Escape") {
                e.stopPropagation();
                setNameDraft(displayName);
                setMode("menu");
              }
            }}
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 focus:border-blue-400 focus:outline-none"
            placeholder={label}
          />
          <div className="mt-1.5 flex items-center justify-end gap-1.5">
            <button
              onClick={() => {
                setNameDraft(displayName);
                setMode("menu");
              }}
              className="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100"
            >
              {t("smartSurveyor.cablesBeta.cancelButton")}
            </button>
            <button
              onClick={submitRename}
              className="flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-blue-700"
            >
              <Check size={12} /> {t("smartSurveyor.cablesBeta.save")}
            </button>
          </div>
        </div>
      ) : mode === "info" ? (
        <div className="space-y-1.5 p-2.5 text-[11px]">
          <div className="mb-1 flex items-center gap-1 font-semibold text-slate-600">
            <Info size={12} /> {t("smartSurveyor.cablesBeta.verteilerInfo")}
          </div>
          <InfoRow label={t("smartSurveyor.cablesBeta.marker")} value={label} />
          <InfoRow
            label={t("smartSurveyor.cablesBeta.name")}
            value={displayName}
          />
          {isTotalShown && (
            <>
              <InfoRow
                label={t("smartSurveyor.cablesBeta.circuitsFed")}
                value={String(stats.circuitCount)}
              />
              <InfoRow
                label={t("smartSurveyor.cablesBeta.totalLength")}
                value={`${formatMetres({ value: round1(stats.totalM), language: i18n.language })} m`}
              />
            </>
          )}
          {provNote && (
            <div className="mt-1 border-t border-slate-100 pt-1.5 text-slate-500">
              <div className="text-slate-400">
                {t("smartSurveyor.cablesBeta.placement")}
              </div>
              <div className="leading-relaxed text-slate-600">
                {provNote.name} — {provNote.placedBy}
              </div>
            </div>
          )}
          <div className="mt-1.5 flex items-center justify-between border-t border-slate-100 pt-1.5">
            <button
              onClick={() => {
                resetSource(label);
                onClose();
              }}
              className="flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
              title={t("smartSurveyor.cablesBeta.resetPositionTooltip")}
            >
              <RotateCcw size={11} />{" "}
              {t("smartSurveyor.cablesBeta.resetPosition")}
            </button>
            <button
              onClick={() => setMode("menu")}
              className="rounded px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100"
            >
              {t("smartSurveyor.cablesBeta.back")}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-1">
          <ActionButton
            icon={<Move size={13} />}
            label={t("smartSurveyor.cablesBeta.moveAction")}
            hint={t("smartSurveyor.cablesBeta.moveHint")}
            onClick={() => {
              trackEvent(
                ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_FEED_POINT_CHANGED,
                {
                  action: "moved",
                },
              );
              startMoveSource(label);
              onClose();
            }}
          />
          <ActionButton
            icon={<Pencil size={13} />}
            label={t("smartSurveyor.cablesBeta.editAction")}
            hint={t("smartSurveyor.cablesBeta.editHint")}
            onClick={() => {
              setNameDraft(displayName);
              setMode("rename");
            }}
          />
          <ActionButton
            icon={<Info size={13} />}
            label={t("smartSurveyor.cablesBeta.infoAction")}
            hint={t("smartSurveyor.cablesBeta.verteilerInfo")}
            onClick={() => setMode("info")}
          />
          <ActionButton
            icon={<Trash2 size={13} />}
            label={t("smartSurveyor.cablesBeta.removeAction")}
            hint={t("smartSurveyor.cablesBeta.removeHint")}
            danger
            onClick={() => {
              trackEvent(
                ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_FEED_POINT_CHANGED,
                {
                  action: "removed",
                },
              );
              removeSource(label);
              onClose();
            }}
          />
        </div>
      )}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  hint,
  danger,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left",
        danger
          ? "text-red-600 hover:bg-red-50"
          : "text-slate-700 hover:bg-slate-50",
      )}
    >
      <span
        className={clsx("shrink-0", danger ? "text-red-500" : "text-slate-400")}
      >
        {icon}
      </span>
      <span className="flex-1">
        <span className="font-medium">{label}</span>
        <span className="ml-1.5 text-[10px] text-slate-400">{hint}</span>
      </span>
    </button>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[84px_1fr] gap-2">
      <div className="text-slate-400">{label}</div>
      <div className="font-medium text-slate-600">{value}</div>
    </div>
  );
}
